import React, { useEffect, useState, memo } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { getContentLoadStatus } from '../../../../content';
/**
 * LoadedModulesWidget — Boot Flow Refactor diagnostics.
 *
 * Shows which of the 10 content modules are currently loaded into memory
 * and which are still pending. Polls every second so admins can watch
 * idle prefetch progress in real-time.
 */
export function LoadedModulesWidget() {
  const [status, setStatus] = useState(() => getContentLoadStatus());
  // F-A2 (19 May 2026): poll only while the tab is visible. When the admin
  // tabs away (or locks their screen) we stop polling and resume on focus —
  // no point hammering `getContentLoadStatus()` every second on a hidden
  // tab. Audit §13 (perf hygiene).
  useEffect(() => {
    if (status.allLoaded) return;
    let intervalId: number | null = null;
    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        const next = getContentLoadStatus();
        setStatus(next);
        if (next.allLoaded && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    };
    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Catch up immediately on resume so the UI isn't stale for up to 1s.
        const next = getContentLoadStatus();
        setStatus(next);
        if (!next.allLoaded) startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [status.allLoaded]);
  const {
    totalModules,
    loadedModuleCodes,
    pendingModuleCodes,
    allLoaded
  } = status;
  const loadedCount = loadedModuleCodes.length;
  const pct = Math.round(loadedCount / totalModules * 100);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6785">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-6786">
        <div className="flex items-center gap-2" data-id="element-6787">
          <div className="w-4 h-4 text-gray-500" data-id="element-6788" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-6789">
            Geladen modules
          </h3>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${allLoaded ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-6790">
          {loadedCount} / {totalModules}
        </span>
      </div>
      <div className="p-4 sm:p-5 space-y-4" data-id="element-6791">
        <div data-id="element-6792">
          <div className="flex justify-between text-xs mb-1.5" data-id="element-6793">
            <span className="text-gray-700 font-medium" data-id="element-6794">Voortgang</span>
            <span className="font-semibold text-gray-600" data-id="element-6795">{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-6796">
            <div className={`h-2 rounded-full transition-all ${allLoaded ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
            width: `${pct}%`
          }} data-id="element-6797" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-6798">
          {loadedModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-800" data-id="element-6799">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6800" />
              <span className="font-mono font-medium" data-id="element-6801">{code}</span>
            </div>)}
          {pendingModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-500" data-id="element-6802">
              <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" data-id="element-6803" />
              <span className="font-mono font-medium" data-id="element-6804">{code}</span>
            </div>)}
        </div>

        {!allLoaded && <p className="text-xs text-gray-500" data-id="element-6805">
            Idle prefetch is bezig op de achtergrond. Modules worden in-place
            aan de indexen toegevoegd zodra ze geladen zijn.
          </p>}
      </div>
    </div>;
}